import type { RenderMode } from '../render/RenderContext'

const defaultVideoGenerationApiBaseUrl = 'http://127.0.0.1:4174'

function normalizeApiBaseUrl(value?: string): string {
  return (value?.trim() || defaultVideoGenerationApiBaseUrl).replace(/\/+$/, '')
}

export const videoGenerationApiBaseUrl = normalizeApiBaseUrl(
  import.meta.env.VITE_VIDEO_GENERATION_API_BASE_URL,
)

export type VideoJobMode = Exclude<RenderMode, 'preview'>
export type VideoJobStatus = 'idle' | 'preparing' | 'rendering' | 'encoding' | 'completed' | 'failed'
export type VideoDataSource = 'REAL' | 'MOCK'

export interface VideoGenerationContext {
  dataSource: VideoDataSource
  date: string
}

export interface DataIntegrityReport {
  complete: boolean
  mode: VideoJobMode
  date: string
  expectedFrameCount: number
  availableFrameCount: number
  firstAvailable: string
  lastAvailable: string
  missing: string[]
  warning: string
}

export interface VideoJobResult {
  mode: VideoJobMode
  date: string
  dataSource: VideoDataSource
  outputPath: string
  outputFile: string
  fps: number
  width: number
  height: number
  durationMs: number
  endHoldDurationMs: number
  marketMinuteDuration: number
  renderedFrameCount: number
  naturalFrameCount: number
  firstTime: string
  lastTime: string
  title: string
  integrity: DataIntegrityReport
}

export interface VideoJob {
  id: string
  mode: VideoJobMode
  date: string
  dataSource: VideoDataSource
  status: VideoJobStatus
  stage: string
  progress: number
  currentFrame: number
  totalFrames: number
  outputFile: string
  error: string
  createdAt: string
  finishedAt: string | null
  result: VideoJobResult | null
}

export class IncompleteVideoDataError extends Error {
  constructor(public readonly report: DataIntegrityReport) {
    super(report.warning)
    this.name = 'IncompleteVideoDataError'
  }
}

export class VideoGenerationService {
  constructor(private readonly baseUrl = videoGenerationApiBaseUrl) {}

  generateNoonVideo(context: VideoGenerationContext, targetDurationSeconds = 16.5, allowIncomplete = false): Promise<VideoJob> {
    return this.generateVideo('noon', context, targetDurationSeconds, allowIncomplete)
  }

  generateCloseVideo(context: VideoGenerationContext, targetDurationSeconds = 16.5, allowIncomplete = false): Promise<VideoJob> {
    return this.generateVideo('close', context, targetDurationSeconds, allowIncomplete)
  }

  async generateVideo(
    mode: VideoJobMode,
    context: VideoGenerationContext,
    targetDurationSeconds = 16.5,
    allowIncomplete = false,
  ): Promise<VideoJob> {
    const response = await fetch(`${this.baseUrl}/api/video/${mode}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...context, allowIncomplete, targetDurationSeconds }),
    })
    const payload = await response.json() as { job?: VideoJob; code?: string; error?: string; report?: DataIntegrityReport }
    if (response.status === 409 && payload.code === 'INCOMPLETE_DATA' && payload.report) {
      throw new IncompleteVideoDataError(payload.report)
    }
    if (!response.ok || !payload.job) throw new Error(payload.error ?? `视频服务响应异常：${response.status}`)
    return payload.job
  }

  async getJob(jobId: string): Promise<VideoJob> {
    const response = await fetch(`${this.baseUrl}/api/video/status/${encodeURIComponent(jobId)}`)
    const payload = await response.json() as { job?: VideoJob; error?: string }
    if (!response.ok || !payload.job) throw new Error(payload.error ?? '无法读取视频任务状态')
    return payload.job
  }

  async openOutputDirectory(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/video/open-output`, { method: 'POST' })
    if (!response.ok) throw new Error('无法打开输出目录')
  }

  getPlaybackUrl(jobId: string): string {
    return `${this.baseUrl}/api/video/file/${encodeURIComponent(jobId)}`
  }
}
